import { PropTypes } from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone';
import { useAppContext } from '../../../providers/AppProvider';
import { injectIntl } from 'react-intl';
import DetergentTank from './DetergentTank';
import _, { map } from 'lodash';
import { selectReadingsFromDate } from '../reducer';
import { useDispatch } from 'react-redux';
import { addDetergentReading } from '../actions';
import ToggleButton from '../../../core/components/ToggleButton';
import { useHistory } from 'react-router-dom';
import { hideDialog, showDialog } from '../../../core/actions';

export const DetergentCharts = ({ intl, detergentContainers, detergentPodId }) => {
  const context = useAppContext();
  const [allowRecord, setAllowRecord] = useState(false);
  const fromDate = useSelector(selectReadingsFromDate);
  const [inputValues, setInputValues] = useState({});
  const [isToggledDirection, setToggledDirection] = useState(null);

  const dispatch = useDispatch();
  const history = useHistory();
  const toggleScrollDirection = () => {
    setToggledDirection((prevState) => !prevState);
  };

  useEffect(() => {
    const today = new Date().toISOString();
    const fromDateMoment = moment.tz(today, context.timeZone);

    if (fromDateMoment.isBefore(moment.tz(context.timeZone).startOf('day'))) {
      setAllowRecord(true);
    } else {
      setAllowRecord(false);
    }
  }, [fromDate, context, context.timeZone]);

  const handleInputChange = (detergentId, value) => {
    const updatedValues = {
      ...inputValues,
      [detergentId]: value,
    };
    setInputValues(updatedValues);
  };

  const handleRecordAll = useCallback(() => {
    const validReadings = [];
    const remainingInputs = {};

    _.forEach(inputValues, (value, detergentId) => {
      const container = detergentContainers.find((c) => c.id === Number(detergentId));
      const capacity = container?.detergentViewData?.capacity;

      if (capacity != null && Number(value) > capacity) {
        remainingInputs[detergentId] = value;
      } else {
        validReadings.push({
          containerId: Number(detergentId),
          value,
          timestamp: moment().utc().toISOString(),
        });
      }
    });

    if (validReadings.length > 0) {
      dispatch(addDetergentReading({ readings: validReadings, detergentPodId }));
    }

    setInputValues(remainingInputs);
  }, [dispatch, detergentContainers, detergentPodId, inputValues]);

  const hasUnsavedReadings = useMemo(
    () => Object.values(inputValues).some((value) => String(value ?? '').trim() !== ''),
    [inputValues]
  );

  // Typing a reading without pressing Enter or Save Readings leaves it in local
  // state only, so navigating away silently discards it. Block the transition
  // and offer to save first.
  useEffect(() => {
    if (!hasUnsavedReadings) return undefined;

    const unblock = history.block((nextLocation) => {
      dispatch(
        showDialog('CONFIRM_DIALOG', {
          title: intl.formatMessage({ id: 'unsavedReadingsTitle' }),
          text: intl.formatMessage({ id: 'unsavedReadings' }),
          submitButtonText: intl.formatMessage({ id: 'saveReadings' }),
          onReady: () => {
            handleRecordAll();
            dispatch(hideDialog());
            unblock();
            history.push(`${nextLocation.pathname}${nextLocation.search || ''}`);
          },
        })
      );
      return false;
    });

    return unblock;
  }, [hasUnsavedReadings, history, dispatch, intl, handleRecordAll]);

  // history.block only covers in-app navigation; a tab close or reload needs the
  // browser's own prompt.
  useEffect(() => {
    if (!hasUnsavedReadings) return undefined;

    const warnBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
      return '';
    };

    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [hasUnsavedReadings]);

  const handleEnter = (event) => {
    if (event.key === 'Enter') {
      handleRecordAll();
    }
  };
  return (
    <div>
      <div className="detergent-save-button-container">
        <ToggleButton
          label={intl.formatMessage({ id: 'toggleScrollDirection' })}
          toggleCallback={toggleScrollDirection}
        />
        <button type="button" className="button" onClick={handleRecordAll}>
          {intl.formatMessage({ id: 'saveReadings' })}
        </button>
      </div>
      <div
        className={`scroll-direction ${
          isToggledDirection === null ? '' : isToggledDirection ? 'toggled' : 'untoggled'
        }`}
        onKeyDown={handleEnter}
      >
        <div className="detergent-tank-container">
          {map(
            [...detergentContainers].sort((a, b) => b.position - a.position),
            (container) => (
              <div key={container.id}>
                <DetergentTank
                  disabledReadings={allowRecord}
                  detergent={container.detergentViewData}
                  detergentPodId={detergentPodId}
                  onInputChange={handleInputChange}
                  inputValue={inputValues[container.id] || ''}
                />
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

DetergentCharts.propTypes = {
  intl: PropTypes.shape({}).isRequired,
  detergentPodId: PropTypes.number.isRequired,
  detergents: PropTypes.arrayOf(PropTypes.object),
  chartData: PropTypes.arrayOf(PropTypes.object),
  asOfDateProps: PropTypes.shape({}).isRequired,
};

export default injectIntl(DetergentCharts);
